import React, {createContext, useContext, useEffect, useState} from 'react';
import {useFetchHook} from "../hooks/useFetchHook";
import fetcher from "../fetcher";
import useDidMountEffect from "../hooks/useDidMountEffect";

const initialPaginationResponse = {
    count: 0,
    next: null,
    previous: null,
    results: [],
    has_more: false,
    current_page: 0
}

export const DealCRMContext = createContext(undefined)

export const DealCRMContextProvider = ( { children, initialSelectedLeads, initialSelectedClients, ...props } ) => {
    const [currentTab, setCurrentTab] = useState('/leads')
    const [selectedLeads, setSelectedLeads] = useState(initialSelectedLeads || [])
    const [selectedClients, setSelectedClients] = useState(initialSelectedClients || [])

    // Leads
    const [fetchListLeadsResponse, fetchListLeads] = useFetchHook(fetcher.getLeads)
    const [listLeadsState, setListLeadsState] = useState(initialPaginationResponse)
    const [createLeadResponse, createLead] = useFetchHook(fetcher.createLead)
    const [updateLeadResponse, updateLead] = useFetchHook(fetcher.updateLead)
    const [deleteLeadResponse, deleteLead] = useFetchHook(fetcher.deleteLead)
    const [bulkUploadLeadsResponse, bulkUploadLeads] = useFetchHook(fetcher.bulkUploadLeads)
    const [leadsSearchValue, setLeadsSearchValue] = useState('')

    // Clients
    const [fetchListClientsResponse, fetchListClients] = useFetchHook(fetcher.getClients)
    const [listClientsState, setListClientsState] = useState(initialPaginationResponse)
    const [createClientResponse, createClient] = useFetchHook(fetcher.createClient)
    const [updateClientResponse, updateClient] = useFetchHook(fetcher.updateClient)
    const [deleteClientResponse, deleteClient] = useFetchHook(fetcher.deleteClient)
    const [bulkUploadClientsResponse, bulkUploadClients] = useFetchHook(fetcher.bulkUploadClients)
    const [clientsSearchValue, setClientsSearchValue] = useState('')

    const [deletedLeadIdsState, setDeletedLeadIds] = useState([])

    const [deletedClientIdsState, setDeletedClientIds] = useState([])

    const buildSearchParams = (value) => {
        return new URLSearchParams({
            s: value,
        }).toString()
    }

    const shouldFetchNextLeads = () => {
        const data = fetchListLeadsResponse?.data?.data
        if (!data?.has_more) return false;
        if (fetchListLeadsResponse?.loading) return false;

        return true
    }

    const fetchNextLeads = () => {
        let queryParams = ''
        if (listLeadsState.current_page !== 0) {
            queryParams += `page=${listLeadsState.current_page + 1}`
        }

        if (leadsSearchValue.length) {
            queryParams += `&${buildSearchParams(leadsSearchValue)}`
        }

        return fetchListLeads(queryParams)
    }

    const removeDeletedLeadFromList = (id) => {
        const newList = listLeadsState.results.filter((lead) => lead.id !== id)
        const newDeletedIds = deletedLeadIdsState
        newDeletedIds.push(id)
        setDeletedLeadIds(newDeletedIds)
        setListLeadsState({...listLeadsState, results: newList})
    }

    useEffect(function fetchListLeadsFromBackendOnMount(){
        fetchNextLeads()
    }, [])

    useDidMountEffect(function updateListLeadsFromBackend(){
        const response = fetchListLeadsResponse?.data?.data

        if (response) {
            const currentPage = response.current_page
            if (currentPage === 1) {
                setListLeadsState(fetchListLeadsResponse?.data?.data)
            } else {
                if (currentPage !== listLeadsState.current_page) {
                    const currentList = listLeadsState.results
                    const data = fetchListLeadsResponse?.data?.data

                    const newList = currentList.concat(data.results)
                    setListLeadsState({...data, results: newList})

                }
            }
        }

    }, [fetchListLeadsResponse])

    useEffect(function addCreatedLeadToList() {
        if (createLeadResponse?.data?.data) {
            const newList = structuredClone(listLeadsState.results)
            newList.unshift(createLeadResponse?.data?.data)
            setListLeadsState({...listLeadsState, results: newList})
        }
    }, [createLeadResponse])

    useEffect(function updateListAfterDelete() {
        if (deleteLeadResponse?.data?.data?.id) {
            removeDeletedLeadFromList(deleteLeadResponse?.data?.data?.id)
        }
    }, [deleteLeadResponse])

    const fetchNextClients = () => {
        let queryParams = ''
        if (listClientsState.current_page !== 0) {
            queryParams += `page=${listClientsState.current_page + 1}`
        }

        if (clientsSearchValue.length) {
            queryParams += `&${buildSearchParams(clientsSearchValue)}`
        }

        return fetchListClients(queryParams)
    }

    const shouldFetchNextClients = () => {
        const data = fetchListClientsResponse?.data?.data
        if (!data?.has_more) return false;
        if (fetchListClientsResponse?.loading) return false;

        return true
    }

    const removeDeletedClientFromList = (id) => {
        const newList = listClientsState.results.filter((client) => client.id !== id)
        const newDeletedIds = deletedClientIdsState
        newDeletedIds.push(id)
        setDeletedClientIds(newDeletedIds)
        setListClientsState({...listClientsState, results: newList})
    }

    useEffect(function fetchListClientsFromBackendOnMount(){
        fetchNextClients()
    }, [])

    useDidMountEffect(function updateListClientsFromBackend(){
        const response = fetchListClientsResponse?.data?.data

        if (response) {
            const currentPage = response.current_page
            if (currentPage === 1) {
                setListClientsState(fetchListClientsResponse?.data?.data)
            } else {
                if (currentPage !== listClientsState.current_page) {
                    const currentList = listClientsState.results
                    const data = fetchListClientsResponse?.data?.data

                    const newList = currentList.concat(data.results)
                    setListClientsState({...data, results: newList})

                }
            }
        }

    }, [fetchListClientsResponse])

    useEffect(function addCreatedClientToList() {
        if (createClientResponse?.data?.data) {
            const newList = structuredClone(listClientsState.results)
            newList.unshift(createClientResponse?.data?.data)
            setListClientsState({...listClientsState, results: newList})
        }
    }, [createClientResponse])

    useEffect(function updateClientListAfterDelete() {
        if (deleteClientResponse?.data?.data?.id) {
            removeDeletedClientFromList(deleteClientResponse?.data?.data?.id)
        }
    }, [deleteClientResponse])


    const value = {
        currentTab,
        setCurrentTab,
        selectedLeads,
        setSelectedLeads,
        selectedClients,
        setSelectedClients,

        // Leads
        listLeadsState,
        fetchNextLeads,
        shouldFetchNextLeads,

        fetchListLeads,
        fetchListLeadsResponse,

        createLead,
        createLeadResponse,

        updateLead,
        updateLeadResponse,

        deleteLead,
        deleteLeadResponse,

        bulkUploadLeads,
        bulkUploadLeadsResponse,

        leadsSearchValue,
        setLeadsSearchValue,

        // Clients

        listClientsState,
        fetchNextClients,
        shouldFetchNextClients,

        fetchListClients,
        fetchListClientsResponse,

        createClient,
        createClientResponse,

        updateClient,
        updateClientResponse,

        deleteClient,
        deleteClientResponse,

        bulkUploadClients,
        bulkUploadClientsResponse,

        clientsSearchValue,
        setClientsSearchValue,

        ...props,
    }

    return (
        <DealCRMContext.Provider value={value}>
            { children }
        </DealCRMContext.Provider>
    )
}

export const useDealCRMContext = () => {
    const context = useContext(DealCRMContext)

    if (context === undefined) {
        throw new Error('useDealCRMContext must be used within DealCRMContext')
    }
    return context
}